import React, {useContext, useEffect, useState} from "react";
import {CreateDeliveryContext} from "../../CreateDeliveryRequest";
import {
    CreditLabel,
    CustomerCreditLabel,
    PickupInformationContainer,
    CustomerPickupInformationContainer,
    PickupInformationHeader,
    CustomerPickupInformationHeader,
    PickupInformationTitle,
    CustomerPickupInformationTitle
} from "./PickupInformation.styles";
import {CircleMarker} from "../../../_defaultComponents/CircleMarker/CircleMarker";
import {InputRow} from "../inputRow/InputRow";

import {Form, Input, Row, Col} from "antd";
import {useTranslation} from "react-i18next";
import {UploadExcelFromTemplate} from "./components/UpdateExcelFromTemplate";
import {formatNumberString} from "../../../../utils/helpers";
import {observer} from "mobx-react-lite";
import {useStore} from "@vigo/hooks";
import SubCompanyInput from "./subCompanyInput/SubCompanyInput";
import CompanyInput from "./companyInput/CompanyInput";

export const PickupInformation = observer(() => {
    const {t} = useTranslation(["newRequest", "subcompanyDetail"]);
    const stores = useStore();
    const {
        createDeliveryRequestForm,
        selectedCompany,
        selectedSubCompany,
        handleSelectCompany,
        handleSelectPickupPoint,
        getError,
        isFromCustomerTheme
    } = useContext(CreateDeliveryContext);

    const [filteredSubCompanyCount, setFilteredSubCompanyCount] = useState(0);

    const activeCompanies = stores.appStore.companyListFilter.filter(company => !company.disabled);
    const singleActiveCompany = activeCompanies.length === 1 ? activeCompanies[0] : null;

    useEffect(() => {
        if (isFromCustomerTheme) return; // müşteri self-servis ekranı kapsam dışı
        if (selectedCompany || !singleActiveCompany) return;
        handleSelectCompany({companyId: singleActiveCompany._id});
    }, [isFromCustomerTheme, singleActiveCompany, selectedCompany, handleSelectCompany]);

    const subCompanyOptionFilter = subCompany => {
        if (!selectedCompany) return true;
        return subCompany.companyId === selectedCompany._id.toString();
    };

    const handleFilteredCountChange = count => {
        setFilteredSubCompanyCount(count);
    };

    // Choose styles based on customer theme
    const ContainerComponent = isFromCustomerTheme
        ? CustomerPickupInformationContainer
        : PickupInformationContainer;
    const HeaderComponent = isFromCustomerTheme
        ? CustomerPickupInformationHeader
        : PickupInformationHeader;
    const TitleComponent = isFromCustomerTheme
        ? CustomerPickupInformationTitle
        : PickupInformationTitle;
    const CreditLabelComponent = isFromCustomerTheme ? CustomerCreditLabel : CreditLabel;

    return (
        <ContainerComponent>
            <HeaderComponent>
                <CircleMarker color="red">1</CircleMarker>
                <TitleComponent>{t("newRequest.fields.pickUp", {ns: "newRequest"})}</TitleComponent>
                {!isFromCustomerTheme &&
                    selectedSubCompany &&
                    stores.meStore.checkPermission("createDeliveryRequest.createOrder") && (
                        <UploadExcelFromTemplate />
                    )}
            </HeaderComponent>
            {
                <InputRow
                    title={t("newRequest.fields.sender", {ns: "newRequest"})}
                    content={
                        <>
                            <Form
                                autoComplete="off"
                                labelCol={{span: 24}}
                                wrapperCol={{span: 24}}
                                scrollToFirstError
                                data-testid="receviverForm"
                                size={isFromCustomerTheme ? "small" : "middle"}
                            >
                                <Row gutter={16}>
                                    <Col span={12}>
                                        <h5>{t("fields.authorizedPerson", {ns: "common"})}</h5>
                                        <Input
                                            value={
                                                selectedSubCompany
                                                    ? selectedSubCompany?.authorizedPerson
                                                    : ""
                                            }
                                            disabled
                                            size={isFromCustomerTheme ? "small" : "middle"}
                                        />
                                    </Col>
                                    <Col span={12}>
                                        <h5>{t("fields.authorizedPhone", {ns: "common"})}</h5>
                                        <Input
                                            value={
                                                selectedSubCompany
                                                    ? selectedSubCompany?.authorizedPhone
                                                    : ""
                                            }
                                            disabled
                                            size={isFromCustomerTheme ? "small" : "middle"}
                                        />
                                    </Col>
                                </Row>
                            </Form>
                        </>
                    }
                />
            }
            <InputRow
                title={t("fields.address", {ns: "common"})}
                content={
                    <>
                        <Form
                            autoComplete="off"
                            labelCol={{span: 24}}
                            wrapperCol={{span: 24}}
                            scrollToFirstError
                            data-testid="pickupForm"
                            size={isFromCustomerTheme ? "small" : "middle"}
                        >
                            {!isFromCustomerTheme && (
                                <CompanyInput
                                    data-testid="PickupInformationContainer_companyInput"
                                    onChange={value => handleSelectCompany({companyId: value})}
                                    value={selectedCompany?._id}
                                    disabled={activeCompanies.length === 1}
                                />
                            )}

                            <Form.Item style={{marginBottom: isFromCustomerTheme ? "4px" : "0px"}}>
                                {isFromCustomerTheme ? (
                                    <>
                                        <h5>{t("fields.subcompany", {ns: "common"})}</h5>
                                        <Input
                                            value={selectedSubCompany?.name || ""}
                                            disabled
                                            style={{
                                                marginBottom: isFromCustomerTheme ? "6px" : "20px"
                                            }}
                                            size={isFromCustomerTheme ? "small" : "middle"}
                                        />
                                    </>
                                ) : (
                                    <SubCompanyInput
                                        data-testid="PickupInformationContainer_subCompanyInput"
                                        onChange={value =>
                                            handleSelectPickupPoint({subCompanyId: value})
                                        }
                                        mb={
                                            getError(`pickupInfos.0.pickupPointId`)
                                                ? "0px"
                                                : isFromCustomerTheme
                                                ? "6px"
                                                : "20px"
                                        }
                                        company={selectedCompany?._id}
                                        initialCall={false}
                                        disabled={!selectedCompany || filteredSubCompanyCount === 1}
                                        optionFilter={subCompanyOptionFilter}
                                        loadWithDelay={500}
                                        value={selectedSubCompany?._id}
                                        onFilteredCountChange={handleFilteredCountChange}
                                    />
                                )}
                            </Form.Item>
                            {selectedSubCompany &&
                            filteredSubCompanyCount > 1 &&
                            Number.isFinite(selectedSubCompany?.credit) &&
                            selectedSubCompany?.credit !== 9999999 &&
                            typeof selectedSubCompany?.credit === "number" ? (
                                <Form.Item
                                    style={{marginBottom: isFromCustomerTheme ? "4px" : "16px"}}
                                >
                                    <h5>Bakiye</h5>
                                    <CreditLabelComponent>
                                        {formatNumberString(selectedSubCompany?.credit)}₺
                                    </CreditLabelComponent>
                                </Form.Item>
                            ) : (
                                <></>
                            )}
                            {
                                <>
                                    <h5>{t("fields.address", {ns: "common"})}</h5>
                                    <Input
                                        value={
                                            selectedSubCompany ? selectedSubCompany?.address : ""
                                        }
                                        disabled
                                        style={{
                                            marginBottom: isFromCustomerTheme ? "6px" : "20px"
                                        }}
                                        size={isFromCustomerTheme ? "small" : "middle"}
                                    />{" "}
                                </>
                            }
                        </Form>
                    </>
                }
            />
        </ContainerComponent>
    );
});
